// Image box <-> sticker. Used by the PSD importer (a layer the admin calls a
// Sticker lands as a real sticker) and by the admin toolbar buttons
// "Make sticker" / "Make photo box".
//
// Each function returns a PATCH, with keys that must go set to `undefined`, so
// it works both as setCurrentObjectProperties' payload (a merge) and spread
// over a plain object. Never dispatch a whole copied object instead: a stale
// copy merged over the store undoes whatever changed since it was read.
//
// ⚠️ Keep this free of psdImport.js imports. The toolbars import this file,
// and psdImport pulls in ag-psd (~90kB, lazy-loaded for admins only).

// Only a finished, hosted image can change type. An empty box has nothing to
// show as a sticker, and a blob: preview (an upload still in flight) must keep
// its pendingImageId so the upload can swap the real URL in.
export const canConvertObject = (obj) =>
  !!obj?.url &&
  !obj.url.startsWith("blob:") &&
  !obj.pendingImageId &&
  !obj.isProcessing;

// A sticker draws its whole bitmap inside its box ("meet"), so the box stays
// exactly where it is: nothing moves and the rotation pivot does not shift.
// Keep Image Fixed goes too, so turning it back into a photo box later gives a
// slot customers can fill.
export const toStickerPatch = (stickerId) => ({
  type: "sticker",
  sticker_id: stickerId || undefined,
  background_id: undefined,
  image: undefined,
  displaySameImage: undefined,
  pendingImageId: undefined,
});

// A photo box COVERS its frame, so it needs the bitmap's real size: the image
// is cover-fitted and centred, the model Photo.jsx and objectResize expect
// (image.width/height = natural size, `scale` carries the fit).
export const toPhotoBoxPatch = (box, natural) => {
  const width = natural?.width || box.width;
  const height = natural?.height || box.height;
  const scale = Math.max(box.width / width, box.height / height);
  return {
    type: "img",
    sticker_id: undefined,
    adjustment: "cover",
    image: {
      width,
      height,
      originalWidth: width,
      originalHeight: height,
      scale,
      positionX: (box.width - width * scale) / 2,
      positionY: (box.height - height * scale) / 2,
    },
  };
};

// The bitmap's natural size, for toPhotoBoxPatch. Null when it cannot load,
// or has not after `timeoutMs` (a stalled request must not leave a caller busy
// forever); the caller then falls back to the box size (an exact fit).
export const loadNaturalSize = (url, timeoutMs = 10000) =>
  new Promise((resolve) => {
    setTimeout(() => resolve(null), timeoutMs);
    // No crossOrigin: only the size is read, and asking for CORS would make a
    // CDN without CORS headers fail the load.
    const img = new Image();
    img.onload = () =>
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = url;
  });
